import React from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { WeezHeader, Card, ContentCard, FilterSetController, Icon, Table, Tooltip, ToastContext, Button } from '@weezevent/nacre';

import { ModerationAttendee as AttendeeModel, ModerationFlowsModel } from '../../models';

import LoadingTable from '../../components/loadingTable';
import { Loader } from 'semantic-ui-react';
import { usePermissions } from '../../user/permissions';
import ModerationPaginator from './components/moderationPaginator';

import Styles from './moderation.module.css';

export default function ModerationFlows({ organization }) {
    const { t } = useTranslation();
    const [currentPage, setCurrentPage] = React.useState(0);
    const [defaultPagination] = React.useState(() => ({ limit: 50, offset: 0 }));
    const [defaultParams] = React.useState(() => ({ search: '', ...defaultPagination }));
    const [queryParams, setQueryParams] = React.useState(() => defaultParams);
    const history = useHistory();
    const permissions = usePermissions();
    const isAdmin = permissions.isModerationAdmin(organization.id);

    const [flows, loadingFlows] = ModerationFlowsModel.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            allow_cache: false,
            query: {
                ...queryParams,
                offset: currentPage * queryParams.limit
            }
        },
        [queryParams, organization, currentPage]
    );

    const handleSearch = React.useCallback(
        value => {
            setQueryParams({ ...queryParams, search: value });
        },
        [queryParams]
    );

    return (
        <>
            <WeezHeader
                title={t('ticket.moderation.dashboard.title')}
                rightComponent={[
                    <Button
                        key={0}
                        primary
                        label={t('ticket.moderation.dashboard.headerCta')}
                        icon={<Icon name="plus" />}
                        onClick={() => history.push(`/ticket/O${organization.id}/moderation/moderation_flows/new`)}
                        disabled={!isAdmin}
                    />
                ]}
            >
                {t('ticket.moderation.dashboard.subtitle')}
            </WeezHeader>

            <Card>
                <ContentCard>
                    <FilterSetController
                        total={flows?.length || '0'}
                        labelTotal={t('ticket.moderation.dashboard.table.countTotal', { count: flows?.length || '-' })}
                        onChange={() => {}}
                        search={{
                            onChange: (_, search = '') => handleSearch(search),
                            handleReset: () => setQueryParams({ ...queryParams, search: '' }),
                            placeholder: t('ticket.moderation.common.searchPlaceholder'),
                            value: queryParams?.search
                        }}
                    />
                </ContentCard>
                <Table useNew>
                    <Table.Header>
                        <Table.Tr>
                            <th>{t('ticket.moderation.dashboard.table.moderation')}</th>
                            <th>{t('ticket.moderation.dashboard.table.pending')}</th>
                            <th>{t('ticket.moderation.dashboard.table.incomplete')}</th>
                            <th>{t('ticket.moderation.dashboard.table.checking')}</th>
                            <th>{t('ticket.moderation.dashboard.table.declined')}</th>
                            <th>{t('ticket.moderation.dashboard.table.validated')}</th>
                            <th>{t('ticket.moderation.dashboard.table.total')}</th>
                            <th>{t('ticket.moderation.dashboard.table.actions')}</th>
                        </Table.Tr>
                    </Table.Header>
                    {flows?.length > 0 && !loadingFlows && (
                        <Table.TBody>
                            {flows?.map(moderation => (
                                <FlowItemRow key={moderation.id} moderation={moderation} organization={organization} />
                            ))}
                        </Table.TBody>
                    )}
                </Table>
                <LoadingTable loading={loadingFlows} height="30vh" />
                {!loadingFlows && !flows?.length && (
                    <Card.EmptyState title={t('ticket.moderation.dashboard.emptyTitle')} subtitle={t('ticket.moderation.dashboard.emptySubtitle')} icon={<Icon name="user" />} />
                )}
                <ModerationPaginator
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalCount={+flows?.__meta?.headers?.get('total-count') || 0}
                    pageSize={queryParams.limit}
                />
            </Card>
        </>
    );
}

const FlowItemRow = ({ moderation, organization }) => {
    const { t } = useTranslation();
    const permissions = usePermissions();
    const history = useHistory();
    const toast = React.useContext(ToastContext);
    const [nextAttendeeLoading, setNextAttendeeLoading] = React.useState(false);
    const isFilesAllowed = permissions.hasCapacity('PyvarCapacity.MODERATION_EXPORTS', organization.id);

    const handleNextAttendee = React.useCallback(() => {
        setNextAttendeeLoading(true);
        AttendeeModel.getNextAttendee({
            organizationId: organization.id,
            flowId: moderation.id,
            query: window.location.search
        })
            .then(response => {
                history.push(response.url);
            })
            .catch(e => {
                toast.error(t('ticket.moderation.attendee.nexAttendeeError'));
            })
            .finally(e => {
                setNextAttendeeLoading(false);
            });
    }, [organization, moderation]);

    const goToModerationFile = React.useCallback(() => {
        if (!isFilesAllowed) {
            return;
        }
        history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${moderation.id}/files/exports`);
    }, [isFilesAllowed, organization.id, moderation.id]);

    return (
        <Table.Tr
            onClick={() => {
                history.push(`/ticket/O${organization.id}/moderation/moderation_flows/${moderation.id}/moderation_attendees`);
            }}
        >
            <Table.Td>
                <div className={Styles['dashboard-table-moderation']}>
                    <span>{moderation.name}</span>
                    <span className={Styles['dashboard-table-moderationEventName']}>{moderation.event_name}</span>
                </div>
            </Table.Td>
            <Table.Td>
                <div className={Styles['dashboard-table-total-container']}>
                    <span>{moderation.stats.count_by_stage.pending}</span>
                    <Icon name="users" />
                </div>
            </Table.Td>
            <Table.Td>
                <div className={Styles['dashboard-table-total-container']}>
                    <span>{moderation.stats.count_by_stage.incomplete}</span>
                    <Icon name="users" />
                </div>
            </Table.Td>
            <Table.Td>
                <div className={Styles['dashboard-table-total-container']}>
                    <span>{moderation.stats.count_by_stage.external}</span>
                    <Icon name="users" />
                </div>
            </Table.Td>
            <Table.Td>
                <div className={Styles['dashboard-table-total-container']}>
                    <span>{moderation.stats.count_by_stage.declined}</span>
                    <Icon name="users" />
                </div>
            </Table.Td>
            <Table.Td>
                <div className={Styles['dashboard-table-total-container']}>
                    <span>{moderation.stats.count_by_stage.validated}</span>
                    <Icon name="users" />
                </div>
            </Table.Td>
            <Table.Td>
                <div className={Styles['dashboard-table-total-container']}>
                    <span>{moderation.stats.total}</span>
                    <Icon name="users" />
                </div>
            </Table.Td>
            <Table.Td>
                <div className={Styles['dashboard-table-actions']}>
                    <Tooltip triggerOnHover title={t('ticket.moderation.dashboard.table.actionUserTooltip')}>
                        {nextAttendeeLoading ? <Loader className={Styles['loader-count-loader']} active /> : <Icon name="user" onClick={handleNextAttendee} />}
                    </Tooltip>
                    {isFilesAllowed && (
                        <Tooltip triggerOnHover title={t('ticket.moderation.dashboard.table.actionExportTooltip')}>
                            <Icon name="File-export" onClick={goToModerationFile} />
                        </Tooltip>
                    )}
                </div>
            </Table.Td>
        </Table.Tr>
    );
};
